import { ApolloError } from "@apollo/client";

// Error codes enum - will be imported from generated types once backend is deployed
export enum ExceptionKeysEnum {
  // Auth errors
  AUTH_ROLE_DENIED = "AUTH_ROLE_DENIED",
  AUTH_WRONG_CREDENTIALS = "AUTH_WRONG_CREDENTIALS",
  AUTH_INVALID_TOKEN = "AUTH_INVALID_TOKEN",
  AUTH_EMAIL_NOT_VERIFIED = "AUTH_EMAIL_NOT_VERIFIED",
  AUTH_TOKEN_EXPIRED = "AUTH_TOKEN_EXPIRED",
  AUTH_REFRESH_TOKEN_INVALID = "AUTH_REFRESH_TOKEN_INVALID",

  // User errors
  USER_NOT_FOUND = "USER_NOT_FOUND",
  USER_DUPLICATE_EMAIL = "USER_DUPLICATE_EMAIL",
  USER_PASSWORD_TOO_WEAK = "USER_PASSWORD_TOO_WEAK",
  USER_CANNOT_DELETE_SELF = "USER_CANNOT_DELETE_SELF",

  // Student errors
  STUDENT_NOT_FOUND = "STUDENT_NOT_FOUND",
  STUDENT_DUPLICATE_EMAIL = "STUDENT_DUPLICATE_EMAIL",
  STUDENT_ALREADY_ENROLLED = "STUDENT_ALREADY_ENROLLED",

  // Test errors
  TEST_NOT_FOUND = "TEST_NOT_FOUND",
  TEST_NOT_EDITABLE = "TEST_NOT_EDITABLE",
  TEST_HAS_UNRESOLVED_COMMENTS = "TEST_HAS_UNRESOLVED_COMMENTS",
  TEST_CANNOT_BE_DELETED = "TEST_CANNOT_BE_DELETED",
  TEST_ALREADY_PUBLISHED = "TEST_ALREADY_PUBLISHED",
  TEST_NOT_PUBLISHED = "TEST_NOT_PUBLISHED",
  TEST_INVALID_STATE_TRANSITION = "TEST_INVALID_STATE_TRANSITION",

  // Question errors
  QUESTION_NOT_FOUND = "QUESTION_NOT_FOUND",
  QUESTION_INVALID_TYPE = "QUESTION_INVALID_TYPE",
  QUESTION_MISSING_CORRECT_ANSWER = "QUESTION_MISSING_CORRECT_ANSWER",
  QUESTION_INVALID_POINTS = "QUESTION_INVALID_POINTS",

  // Assessment/Attempt errors
  ASSESSMENT_NOT_FOUND = "ASSESSMENT_NOT_FOUND",
  ATTEMPT_NOT_FOUND = "ATTEMPT_NOT_FOUND",
  ATTEMPT_ALREADY_SUBMITTED = "ATTEMPT_ALREADY_SUBMITTED",
  ATTEMPT_TIME_EXPIRED = "ATTEMPT_TIME_EXPIRED",
  ATTEMPT_NOT_STARTED = "ATTEMPT_NOT_STARTED",
  ATTEMPT_CANNOT_BE_MODIFIED = "ATTEMPT_CANNOT_BE_MODIFIED",

  // Competition Year errors
  COMPETITION_YEAR_NOT_FOUND = "COMPETITION_YEAR_NOT_FOUND",
  COMPETITION_YEAR_DUPLICATE = "COMPETITION_YEAR_DUPLICATE",
  COMPETITION_YEAR_INVALID_DATES = "COMPETITION_YEAR_INVALID_DATES",

  // Role errors
  ROLE_NOT_FOUND = "ROLE_NOT_FOUND",
  ROLE_DUPLICATE_NAME = "ROLE_DUPLICATE_NAME",
  ROLE_CANNOT_DELETE_IN_USE = "ROLE_CANNOT_DELETE_IN_USE",

  // Validation errors
  VALIDATION_ERROR = "VALIDATION_ERROR",
  VALIDATION_NO_DATA_SUBMITTED = "VALIDATION_NO_DATA_SUBMITTED",
  VALIDATION_INVALID_FILE_TYPE = "VALIDATION_INVALID_FILE_TYPE",
  VALIDATION_FILE_TOO_LARGE = "VALIDATION_FILE_TOO_LARGE",

  // Storage errors
  STORAGE_UPLOAD_FAILED = "STORAGE_UPLOAD_FAILED",
  STORAGE_DELETE_FAILED = "STORAGE_DELETE_FAILED",
  STORAGE_FILE_NOT_FOUND = "STORAGE_FILE_NOT_FOUND",

  // Email errors
  EMAIL_SEND_FAILED = "EMAIL_SEND_FAILED",
  EMAIL_INVALID_TEMPLATE = "EMAIL_INVALID_TEMPLATE",

  // Export errors
  EXPORT_GENERATION_FAILED = "EXPORT_GENERATION_FAILED",
  EXPORT_INVALID_FORMAT = "EXPORT_INVALID_FORMAT",

  // Identity errors
  IDENTITY_NOT_FOUND = "IDENTITY_NOT_FOUND",
  IDENTITY_DUPLICATE = "IDENTITY_DUPLICATE",

  // Generic errors
  INTERNAL_SERVER_ERROR = "INTERNAL_SERVER_ERROR",
  BAD_REQUEST = "BAD_REQUEST",
  UNAUTHORIZED = "UNAUTHORIZED",
  FORBIDDEN = "FORBIDDEN",
  NOT_FOUND = "NOT_FOUND",
  CONFLICT = "CONFLICT",
}

// Czech translations for all error codes
const errorTexts: Record<ExceptionKeysEnum, string> = {
  // Auth errors
  [ExceptionKeysEnum.AUTH_ROLE_DENIED]:
    "Nemáte oprávnění k provedení této akce",
  [ExceptionKeysEnum.AUTH_WRONG_CREDENTIALS]:
    "Nesprávné přihlašovací údaje. Zkuste to prosím znovu.",
  [ExceptionKeysEnum.AUTH_INVALID_TOKEN]: "Neplatný přihlašovací token",
  [ExceptionKeysEnum.AUTH_EMAIL_NOT_VERIFIED]:
    "E-mail nebyl ověřen. Zkontrolujte prosím svou e-mailovou schránku.",
  [ExceptionKeysEnum.AUTH_TOKEN_EXPIRED]:
    "Přihlášení vypršelo. Přihlaste se prosím znovu.",
  [ExceptionKeysEnum.AUTH_REFRESH_TOKEN_INVALID]:
    "Neplatný refresh token. Přihlaste se prosím znovu.",

  // User errors
  [ExceptionKeysEnum.USER_NOT_FOUND]: "Uživatel nenalezen",
  [ExceptionKeysEnum.USER_DUPLICATE_EMAIL]:
    "Uživatel s tímto e-mailem již existuje",
  [ExceptionKeysEnum.USER_PASSWORD_TOO_WEAK]:
    "Heslo je příliš slabé. Použijte kombinaci velkých a malých písmen, číslic a speciálních znaků.",
  [ExceptionKeysEnum.USER_CANNOT_DELETE_SELF]: "Nemůžete smazat sami sebe",

  // Student errors
  [ExceptionKeysEnum.STUDENT_NOT_FOUND]: "Student nenalezen",
  [ExceptionKeysEnum.STUDENT_DUPLICATE_EMAIL]:
    "Student s tímto e-mailem již existuje",
  [ExceptionKeysEnum.STUDENT_ALREADY_ENROLLED]:
    "Student je již přihlášen k této soutěži",

  // Test errors
  [ExceptionKeysEnum.TEST_NOT_FOUND]: "Test nenalezen",
  [ExceptionKeysEnum.TEST_NOT_EDITABLE]:
    "Test nelze upravovat. Pouze testy ve stavu návrh lze upravovat.",
  [ExceptionKeysEnum.TEST_HAS_UNRESOLVED_COMMENTS]:
    "Test nelze publikovat, protože obsahuje nevyřešené komentáře",
  [ExceptionKeysEnum.TEST_CANNOT_BE_DELETED]:
    "Test nelze smazat. Pravděpodobně již byl použit studenty.",
  [ExceptionKeysEnum.TEST_ALREADY_PUBLISHED]: "Test je již publikován",
  [ExceptionKeysEnum.TEST_NOT_PUBLISHED]: "Test není publikován",
  [ExceptionKeysEnum.TEST_INVALID_STATE_TRANSITION]:
    "Neplatná změna stavu testu",

  // Question errors
  [ExceptionKeysEnum.QUESTION_NOT_FOUND]: "Otázka nenalezena",
  [ExceptionKeysEnum.QUESTION_INVALID_TYPE]: "Neplatný typ otázky",
  [ExceptionKeysEnum.QUESTION_MISSING_CORRECT_ANSWER]:
    "Otázka nemá definovanou správnou odpověď",
  [ExceptionKeysEnum.QUESTION_INVALID_POINTS]: "Neplatný počet bodů pro otázku",

  // Assessment/Attempt errors
  [ExceptionKeysEnum.ASSESSMENT_NOT_FOUND]: "Hodnocení nenalezeno",
  [ExceptionKeysEnum.ATTEMPT_NOT_FOUND]: "Pokus nenalezen",
  [ExceptionKeysEnum.ATTEMPT_ALREADY_SUBMITTED]:
    "Tento pokus již byl odeslán a nelze jej změnit",
  [ExceptionKeysEnum.ATTEMPT_TIME_EXPIRED]: "Čas pro dokončení testu vypršel",
  [ExceptionKeysEnum.ATTEMPT_NOT_STARTED]: "Pokus ještě nebyl zahájen",
  [ExceptionKeysEnum.ATTEMPT_CANNOT_BE_MODIFIED]: "Tento pokus nelze upravovat",

  // Competition Year errors
  [ExceptionKeysEnum.COMPETITION_YEAR_NOT_FOUND]: "Soutěžní ročník nenalezen",
  [ExceptionKeysEnum.COMPETITION_YEAR_DUPLICATE]:
    "Soutěžní ročník s tímto názvem již existuje",
  [ExceptionKeysEnum.COMPETITION_YEAR_INVALID_DATES]:
    "Neplatné datum pro soutěžní ročník",

  // Role errors
  [ExceptionKeysEnum.ROLE_NOT_FOUND]: "Role nenalezena",
  [ExceptionKeysEnum.ROLE_DUPLICATE_NAME]: "Role s tímto názvem již existuje",
  [ExceptionKeysEnum.ROLE_CANNOT_DELETE_IN_USE]:
    "Nelze smazat roli, která je aktuálně používána",

  // Validation errors
  [ExceptionKeysEnum.VALIDATION_ERROR]:
    "Chyba validace. Zkontrolujte prosím zadané údaje.",
  [ExceptionKeysEnum.VALIDATION_NO_DATA_SUBMITTED]:
    "Nebyla odeslána žádná data",
  [ExceptionKeysEnum.VALIDATION_INVALID_FILE_TYPE]: "Neplatný typ souboru",
  [ExceptionKeysEnum.VALIDATION_FILE_TOO_LARGE]: "Soubor je příliš velký",

  // Storage errors
  [ExceptionKeysEnum.STORAGE_UPLOAD_FAILED]: "Nahrání souboru se nezdařilo",
  [ExceptionKeysEnum.STORAGE_DELETE_FAILED]: "Smazání souboru se nezdařilo",
  [ExceptionKeysEnum.STORAGE_FILE_NOT_FOUND]: "Soubor nenalezen",

  // Email errors
  [ExceptionKeysEnum.EMAIL_SEND_FAILED]: "Odeslání e-mailu se nezdařilo",
  [ExceptionKeysEnum.EMAIL_INVALID_TEMPLATE]: "Neplatná e-mailová šablona",

  // Export errors
  [ExceptionKeysEnum.EXPORT_GENERATION_FAILED]:
    "Generování exportu se nezdařilo",
  [ExceptionKeysEnum.EXPORT_INVALID_FORMAT]: "Neplatný formát exportu",

  // Identity errors
  [ExceptionKeysEnum.IDENTITY_NOT_FOUND]: "Identita nenalezena",
  [ExceptionKeysEnum.IDENTITY_DUPLICATE]:
    "Identita s těmito údaji již existuje",

  // Generic errors
  [ExceptionKeysEnum.INTERNAL_SERVER_ERROR]:
    "Nastala vnitřní chyba serveru. Zkuste to prosím později.",
  [ExceptionKeysEnum.BAD_REQUEST]:
    "Neplatný požadavek. Zkontrolujte prosím zadané údaje.",
  [ExceptionKeysEnum.UNAUTHORIZED]: "Nejste přihlášeni. Přihlaste se prosím.",
  [ExceptionKeysEnum.FORBIDDEN]: "Nemáte oprávnění k provedení této akce",
  [ExceptionKeysEnum.NOT_FOUND]: "Požadovaný zdroj nebyl nalezen",
  [ExceptionKeysEnum.CONFLICT]: "Konflikt dat. Operaci nelze provést.",
};

const formatToString = ({
  apolloError,
  fallback,
}: {
  apolloError: ApolloError | undefined;
  fallback?: string;
}): string => {
  const error = apolloError?.graphQLErrors[0];

  if (!error) {
    return fallback ?? "Neznámá chyba";
  }

  return (
    errorTexts[error.message as ExceptionKeysEnum] ?? fallback ?? error.message
  );
};

const getKey = ({
  apolloError,
}: {
  apolloError: ApolloError | undefined;
}): ExceptionKeysEnum | null => {
  const error = apolloError?.graphQLErrors[0];

  if (!error) {
    return null;
  }

  return error.message as ExceptionKeysEnum;
};

const isKey = ({
  apolloError,
  key,
}: {
  apolloError: ApolloError | undefined;
  key: ExceptionKeysEnum;
}): boolean => {
  return getKey({ apolloError }) === key;
};

const errorUtils = {
  errorTexts,
  formatToString,
  getKey,
  isKey,
  ExceptionKeysEnum,
};

export default errorUtils;
